import { useRef } from "react";
import { uniqueId } from "lodash";
import { t } from "@/i18n-js/instance";
import { ConfirmationModal } from "@circle-react-shared/ModalsV2/ConfirmationModal";
import type { OnSubmitFunction } from "@circle-react-shared/uikit/Form/Form";
import { Form } from "@circle-react-uikit/Form";
import { CreateTemplateContent } from "./CreateTemplateContent";
import { UpdateTemplateContent } from "./UpdateTemplateContent";
import { templateLocaleNamespace } from "./UpdateTemplateContent";
import type { Mode } from "./useUpsertTemplateModal";
import { useUpsertTemplates } from "./useUpsertTemplates";
import { createTemplateValidationSchema } from "./validationSchema";

export type TemplateType = "updateTemplate" | "createTemplate";

export const UpsertTemplateModal = ({
  mode,
  onClose,
  closeEditor,
}: {
  mode: Mode | null;
  onClose: () => void;
  closeEditor?: () => void;
}) => {
  const { current: formId } = useRef(uniqueId("template_name_"));
  const nameInputRef = useRef<HTMLInputElement>(null);

  const { handleUpsert, templateName: currentTemplateName } =
    useUpsertTemplates();

  const handleSubmit: OnSubmitFunction<{
    name: string;
    template_type: TemplateType;
  }> = async values => {
    await handleUpsert({
      name: (values.name || currentTemplateName) ?? "",
      templateType: values.template_type,
    });
    onClose();
    closeEditor?.();
  };

  const renderModalContent = () => {
    if (mode === "create_only") {
      return <CreateTemplateContent nameInputRef={nameInputRef} />;
    }
    if (mode === "upsert") {
      return <UpdateTemplateContent templateName={currentTemplateName ?? ""} />;
    }
    return null;
  };

  if (!mode) {
    return null;
  }

  return (
    <ConfirmationModal
      formId={formId}
      isOpen
      onClose={onClose}
      title={t([templateLocaleNamespace, "save_template"])}
      confirmVariant="circle"
      confirmText={t([templateLocaleNamespace, "save_template"])}
    >
      <Form
        id={formId}
        mode="onChange"
        validationSchema={
          mode === "create_only" ? createTemplateValidationSchema : undefined
        }
        onSubmit={handleSubmit}
        defaultValues={{
          template_type: "updateTemplate",
        }}
      >
        {renderModalContent()}
      </Form>
    </ConfirmationModal>
  );
};
